import { DataManagementConfig } from '@shared/components/data-management';
import { AddAdminDialog } from '../components/AddAdminDialog';
import { AdminUserTableData, AdminInviteFormData } from '../types';
import { useSystemAdminsTableData, useInviteAdmin } from '../hooks/useSystemAdmins';
import { RoleChip } from '@shared/components/RoleChip';
import { userInvitationApi } from '@features/user-invitation/api/userInvitationApi';
import { notificationService } from '@shared/components/notifications';

export const createSystemAdminsConfig = (
  removeAdminMutation: any,
  _confirmFn: any
): DataManagementConfig<AdminUserTableData, AdminInviteFormData, never> => ({
  hooks: {
    useQuery: () => useSystemAdminsTableData(),
    useCreate: () => useInviteAdmin(),
    useDelete: removeAdminMutation ? () => removeAdminMutation : undefined,
  },

  table: {
    title: 'System Administrators',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      {
        key: 'role',
        label: 'Role',
        render: (role) =>
          role && typeof role === 'string' ? <RoleChip role={role} size="small" /> : null,
      },
      {
        key: 'assignedAt',
        label: 'Assigned At',
        render: (date) => (date ? new Date(date).toLocaleDateString() : null),
      },
      {
        key: 'status',
        label: 'Status',
        render: (status) => (status === 'active' ? 'Active' : 'Invited'),
      },
    ],
    emptyMessage: 'No system administrators found',
    errorMessage: 'Failed to load system administrators. Please try refreshing the page.',
  },

  actions: {
    create: {
      label: 'Add Admin User',
      dialog: AddAdminDialog,
    },
    delete: removeAdminMutation
      ? {
          label: 'Remove',
          confirmTitle: 'Remove Admin',
          confirmMessage: (admin) => `Remove ${admin.name || admin.email} as system administrator?`,
          transformParams: (admin) => ({
            userId: admin.userId,
            userRoleId: admin.userRoleId,
            userName: admin.name || admin.email,
          }),
        }
      : undefined,
    custom: [
      {
        label: 'Resend Invite',
        onClick: async (admin) => {
          try {
            const response = await userInvitationApi.resendInvitation(admin.email);
            if (response.data?.success) {
              notificationService.success(`Invitation resent to ${admin.email}`);
            } else {
              notificationService.error(
                response.data?.errorMessage || response.message || 'Failed to resend invitation'
              );
            }
          } catch (error) {
            console.error('Failed to resend invitation:', error);
            notificationService.error('Failed to resend invitation');
          }
        },
        color: 'primary',
        variant: 'text',
        condition: (admin) => admin.status === 'invited',
      },
    ],
  },

  features: {
    search: true,
    pagination: true,
  },
});
